'use client'

import { useRef } from 'react'
import { motion, useInView, useReducedMotion } from 'framer-motion'
import ReactMarkdown from 'react-markdown'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
} from '@mdxui/primitives'
import { cn } from '../../lib/cn'
import { Markdown } from '../../lib/markdown'
import {
  sectionVariants,
  slideFromLeftVariants,
  slideFromRightVariants,
  reducedMotionVariants,
} from '../../lib/motion'

interface FAQItem {
  question: string
  answer: string
}

/**
 * Props for FAQSplitCTA component
 *
 * @remarks
 * **IMPORTANT CONSTRAINTS FOR AI/PROGRAMMATIC GENERATION:**
 * - Split layout: CTA left, accordion right
 * - Left side has heading, description, and CTA button
 * - Answers support **Markdown** (bold, lists, inline code, links, paragraphs)
 * - 4-6 items recommended for balanced layout
 */
export interface FAQSplitCTAProps {
  title?: string
  description?: string
  supportLink?: { text: string; url: string }
  secondaryButton?: { text: string; url: string }
  items?: FAQItem[]
  className?: string
}

/**
 * FAQSplitCTA - Split layout with CTA and accordion
 *
 * **Layout:**
 * ```
 * ┌─────────────────────────────────────────────┐
 * │  Frequently Asked        │ ▸ Question 1?    │
 * │  Questions               │ ▸ Question 2?    │
 * │                          │ ▾ Question 3?    │
 * │  Description with link   │   Answer...      │
 * │                          │ ▸ Question 4?    │
 * │  [View Docs]             │                  │
 * └─────────────────────────────────────────────┘
 * ```
 *
 * @example
 * ```tsx
 * <FAQSplitCTA
 *   title="Frequently Asked Questions"
 *   description="Still have questions?"
 *   secondaryButton={{ text: 'View Docs', url: '/docs' }}
 *   items={[
 *     { question: 'What is mdxui?', answer: 'A **type-safe** component library.' },
 *   ]}
 * />
 * ```
 */
const FAQSplitCTA = ({
  title = 'Frequently Asked Questions',
  description = 'Still have questions? Feel free to contact our developer documentation.',
  supportLink,
  secondaryButton = { text: 'View Docs', url: '/docs' },
  items = [],
  className,
}: FAQSplitCTAProps) => {
  const sectionRef = useRef<HTMLElement>(null)
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' })
  const prefersReducedMotion = useReducedMotion()

  const leftAnimation = prefersReducedMotion
    ? reducedMotionVariants
    : slideFromLeftVariants
  const rightAnimation = prefersReducedMotion
    ? reducedMotionVariants
    : slideFromRightVariants

  return (
    <motion.section
      ref={sectionRef}
      className={cn('py-16 lg:py-32', className)}
      variants={sectionVariants}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
    >
      <div className="container max-w-7xl mx-auto">
        <div className="mx-auto grid max-w-7xl gap-10 md:grid-cols-2">
          <motion.div
            className="flex flex-col gap-6"
            variants={leftAnimation}
          >
            <h2 className="text-3xl font-semibold lg:text-4xl">{title}</h2>
            <p className="text-muted-foreground lg:text-lg">
              <Markdown inline>{description}</Markdown>
              {supportLink && (
                <a
                  href={supportLink.url}
                  className="mx-1 whitespace-nowrap text-primary underline"
                >
                  {supportLink.text}
                </a>
              )}
            </p>
            {secondaryButton && (
              <Button size="lg" variant="outline" className="w-full md:w-fit" asChild>
                <a href={secondaryButton.url}>{secondaryButton.text}</a>
              </Button>
            )}
          </motion.div>
          <motion.div variants={rightAnimation}>
            <Accordion type="multiple">
              {items.map((faq, index) => (
                <AccordionItem key={faq.question} value={`item-${index}`}>
                  <AccordionTrigger className="text-left font-semibold hover:no-underline">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="prose prose-sm max-w-none text-muted-foreground prose-headings:text-foreground prose-strong:text-foreground prose-code:rounded-[var(--radius-sm,calc(var(--radius)*0.5))] prose-code:bg-muted prose-code:px-1.5 prose-code:py-0.5 prose-code:font-normal prose-code:text-foreground prose-code:before:content-none prose-code:after:content-none prose-pre:bg-muted prose-pre:text-foreground prose-a:text-primary prose-a:no-underline hover:prose-a:underline">
                      <ReactMarkdown>{faq.answer}</ReactMarkdown>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </div>
    </motion.section>
  )
}

export { FAQSplitCTA }
